import React from 'react';
import { ExternalLink, MapPin, Wifi, X } from 'lucide-react';
import { MenuLanguageFlagsBar } from './MenuFlag';
import { FacebookIcon, InstagramIcon, TikTokIcon } from './SocialBrandIcons';

function LinkButton({ href, icon, label, sublabel, testId }) {
    if (!href) return null;
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="client-info-link"
            data-testid={testId}
        >
            <span className="client-info-link__icon">{icon}</span>
            <span className="client-info-link__text">
                <strong>{label}</strong>
                {sublabel && <span>{sublabel}</span>}
            </span>
            <ExternalLink size={14} className="client-info-link__arrow" />
        </a>
    );
}

const SOCIAL_LINKS = [
    { key: 'facebook_url', label: 'Facebook', Icon: FacebookIcon, testId: 'client-info-facebook', className: 'client-info-social__btn--facebook' },
    { key: 'instagram_url', label: 'Instagram', Icon: InstagramIcon, testId: 'client-info-instagram', className: 'client-info-social__btn--instagram' },
    { key: 'tiktok_url', label: 'TikTok', Icon: TikTokIcon, testId: 'client-info-tiktok', className: 'client-info-social__btn--tiktok' },
];

export function ClientRestaurantInfoModal({
    open,
    onClose,
    t,
    clientInfo = {},
    schedulePills = [],
    enabledLangs,
    lang,
    onChangeLang,
}) {
    if (!open) return null;

    const whatsapp = clientInfo.whatsapp;
    const whatsappHref = whatsapp
        ? `https://wa.me/${String(whatsapp).replace(/\D/g, '')}`
        : null;

    const socialLinks = SOCIAL_LINKS.filter(({ key }) => clientInfo[key]);

    return (
        <div className="modal-backdrop" onClick={onClose} data-testid="client-info-modal">
            <div className="modal client-info-modal" onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h2 className="headline" style={{ fontSize: '1.6rem', margin: 0 }}>{t('detailsTitle')}</h2>
                    <button type="button" className="modal-close" onClick={onClose} aria-label={t('ok')}>
                        <X size={18} />
                    </button>
                </div>

                <div className="client-info-links">
                    <LinkButton
                        href={clientInfo.tripadvisor_url}
                        icon={<span style={{ fontSize: '1.1rem' }}>🦉</span>}
                        label="TripAdvisor"
                        testId="client-info-tripadvisor"
                    />
                    <LinkButton
                        href={whatsappHref}
                        icon={<span style={{ fontSize: '1.1rem' }}>💬</span>}
                        label="WhatsApp"
                        sublabel={whatsapp}
                        testId="client-info-whatsapp"
                    />
                    <LinkButton
                        href={clientInfo.google_maps_url}
                        icon={<span style={{ fontSize: '1.1rem' }}>📍</span>}
                        label="Google Maps"
                        testId="client-info-maps"
                    />
                </div>

                {socialLinks.length > 0 && (
                    <div className="client-info-social" data-testid="client-info-social">
                        {socialLinks.map(({ key, label, Icon, testId, className }) => (
                            <a
                                key={key}
                                href={clientInfo[key]}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`client-info-social__btn ${className}`}
                                aria-label={label}
                                title={label}
                                data-testid={testId}
                            >
                                <Icon size={22} />
                            </a>
                        ))}
                    </div>
                )}

                {schedulePills.length > 0 && (
                    <div className="client-info-hours" data-testid="client-info-hours">
                        {schedulePills.map((pill) => (
                            <div key={`${pill.label}-${pill.hours}`} className="client-info-hour-pill">
                                <span className="client-info-hour-pill__label">{pill.label}</span>
                                <span className="client-info-hour-pill__hours">{pill.hours}</span>
                            </div>
                        ))}
                    </div>
                )}

                {(clientInfo.wifi_name || clientInfo.wifi_password) && (
                    <div className="client-info-wifi" data-testid="client-info-wifi">
                        <Wifi size={18} color="var(--neon-cyan)" />
                        <div>
                            <strong>{clientInfo.wifi_name || t('wifi')}</strong>
                            {clientInfo.wifi_password && (
                                <div style={{ color: 'var(--text-muted)', fontSize: '0.88rem', marginTop: 2 }}>
                                    {t('wifiPassword')}: {clientInfo.wifi_password}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {clientInfo.address && (
                    <div className="client-info-address" data-testid="client-info-address">
                        <MapPin size={16} color="var(--amber)" />
                        <span>{clientInfo.address}</span>
                    </div>
                )}

                <div className="client-info-lang">
                    <div className="client-info-lang__label">{t('language')}</div>
                    <MenuLanguageFlagsBar
                        enabled={enabledLangs}
                        value={lang}
                        onChange={onChangeLang}
                        label={t('language')}
                    />
                </div>
            </div>
        </div>
    );
}
